import {
  ActivatedRouteSnapshot,
  DetachedRouteHandle,
  RouteReuseStrategy,
} from '@angular/router';

export class CustomRouteReuseStrategy implements RouteReuseStrategy {
  private storedHandles = new Map<string, DetachedRouteHandle>();

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return this.isReusable(route);
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    if (!handle) {
      return;
    }
    const key = this.getKey(route);
    if (key) {
      this.storedHandles.set(key, handle);
    }
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    const key = this.getKey(route);
    return !!key && this.storedHandles.has(key);
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    const key = this.getKey(route);
    if (!key) {
      return null;
    }
    return this.storedHandles.get(key) ?? null;
  }

  shouldReuseRoute(
    future: ActivatedRouteSnapshot,
    curr: ActivatedRouteSnapshot,
  ): boolean {
    return future.routeConfig === curr.routeConfig;
  }

  private isReusable(route: ActivatedRouteSnapshot): boolean {
    const parentPath = route.parent?.routeConfig?.path;
    const grandParentPath = route.parent?.parent?.routeConfig?.path;
    const path = route.routeConfig?.path ?? '';

    // Home section - user list / TOMASS summary (direct child of home/:sectionId)
    if (parentPath === 'home/:sectionId') {
      return path === 'users' || path === 'tomass-summary';
    }

    // Home section - user record list / media list (nested under users/:userId via UserDetailLayout)
    if (parentPath === 'users/:userId') {
      return path === 'records' || path === 'medias';
    }

    // Home section - GOST record list (nested under users/:userId/gost)
    if (parentPath === 'gost') {
      return path === 'records' || path === 'progress';
    }

    // Admin section - list pages (preserve filter state when navigating to detail/create pages)
    // Route structure: section-admin > :sectionId > staff-list
    if (grandParentPath === 'section-admin' && parentPath === ':sectionId') {
      return (
        path === 'staff-list' ||
        path === 'contributor-list' ||
        path === 'supervisor-list' ||
        path === 'user-list' ||
        path === 'records-download-requests'
      );
    }

    return false;
  }

  private getKey(route: ActivatedRouteSnapshot): string | null {
    if (!this.isReusable(route)) {
      return null;
    }

    const paths: string[] = [];
    route.pathFromRoot.forEach(r => {
      const p = r.routeConfig?.path;
      if (p) {
        paths.push(p);
      }
    });

    const params = route.pathFromRoot.reduce<Record<string, string>>(
      (acc, r) => {
        Object.entries(r.params ?? {}).forEach(([k, v]) => {
          acc[k] = String(v);
        });
        return acc;
      },
      {},
    );

    const paramsPart = Object.keys(params)
      .sort()
      .map(k => `${k}:${params[k]}`)
      .join('|');

    const pathPart = paths.join('/');
    return paramsPart ? `${pathPart}?${paramsPart}` : pathPart;
  }
}
